import { useState } from "react";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { streamSSE } from "@/lib/api";
import { ideasStore, useIdeas } from "@/lib/storage";
import { IdeaCard } from "@/components/IdeaCard";
import { IdeaInsights } from "@/components/IdeaInsights";
import { StreamConsole } from "@/components/StreamConsole";

const fieldCls = "w-full rounded-xl bg-[#090A0F] border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500/60 transition-colors";

export default function Validate() {
  const [form, setForm] = useState({ nome: "", descrizione: "", target: "" });
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [resultId, setResultId] = useState(null);
  const result = useIdeas().find((i) => i.id === resultId);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.nome.trim().length < 2 || form.descrizione.trim().length < 10) {
      toast.error("Inserisci un nome e una descrizione di almeno 10 caratteri.");
      return;
    }
    setLoading(true);
    setStreamText("");
    try {
      const res = await streamSSE("/ideas/validate", form, (t) => setStreamText((s) => s + t));
      ideasStore.upsert([res.idea]);
      setResultId(res.idea.id);
      toast.success("Valutazione completata");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="validate-page">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline-label">Verifica la tua idea</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]">Hai già un'idea? Mettiamola alla prova.</h1>
          <p className="text-slate-400 mt-5 leading-relaxed">Descrivila con parole tue. L'AI la valuta con gli stessi 5 criteri, ti dice punti di forza, rischi e come migliorarla.</p>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 rounded-3xl bg-[#12141F] border border-white/[0.07] p-6 sm:p-8 space-y-5" data-testid="validate-form">
          <div>
            <label className="text-sm font-medium text-slate-300">Nome dell'idea</label>
            <input value={form.nome} onChange={set("nome")} className={`${fieldCls} mt-2`} placeholder="Es. Box mensile di snack regionali" data-testid="input-custom-idea-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300">Descrizione</label>
            <textarea value={form.descrizione} onChange={set("descrizione")} rows={5} className={`${fieldCls} mt-2 resize-none`} placeholder="Come funziona, cosa offri, perché è diversa…" data-testid="input-custom-idea-description" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300">Target e come pensi di guadagnare <span className="text-slate-500">(facoltativo)</span></label>
            <textarea value={form.target} onChange={set("target")} rows={2} className={`${fieldCls} mt-2 resize-none`} placeholder="Es. famiglie 30-50 anni, abbonamento €29/mese" data-testid="input-custom-idea-target" />
          </div>
          <button type="submit" disabled={loading} data-testid="btn-validate-custom-idea"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 font-semibold text-slate-950 transition-colors duration-200 hover:bg-amber-400 disabled:opacity-50">
            <ClipboardCheck className="w-4 h-4" /> {loading ? "Valutazione in corso…" : "Valuta la mia idea"}
          </button>
        </form>
      </div>
      <section className="mt-14 space-y-6" data-testid="validate-result">
        {loading && <StreamConsole text={streamText} title="L'AI sta analizzando la tua idea" />}
        {!loading && result && (
          <>
            <div className="max-w-xl"><IdeaCard idea={result} testId="validated-idea-card" /></div>
            <IdeaInsights idea={result} />
          </>
        )}
      </section>
    </div>
  );
}
